import React from 'react'
import { Box, Stack, alpha } from '@mui/material'
import dayjs from 'dayjs'

import { Typography } from '~components/internal'
import { DAYS_OF_WEEK, type EnrichedAIPhoneLineConfig } from '~types'

const HOUR_HEIGHT = 32

const toMinutes = (t?: string): number => {
  const [h = 0, m = 0] = (t || '').split(':').map(Number)
  return h * 60 + m
}

type ConfigBlock = { configIndex: number; start: number; end: number }
type ConfigBlockLayout = ConfigBlock & { column: number; numColumns: number }

const overlaps = (a: ConfigBlock, b: ConfigBlock): boolean => a.start < b.end && b.start < a.end

const computeBlockLayouts = (blocks: ConfigBlock[]): ConfigBlockLayout[] => {
  const n = blocks.length
  const blocksSeen = new Set<number>()
  const blockGroups: number[][] = []
  for (let i = 0; i < n; i++) {
    if (blocksSeen.has(i)) continue
    const blockGroup: number[] = []
    const blocksToCheck = [i]
    while (blocksToCheck.length) {
      const currentBlock = blocksToCheck.shift()!
      if (blocksSeen.has(currentBlock)) continue
      blocksSeen.add(currentBlock)
      blockGroup.push(currentBlock)
      for (let j = i + 1; j < n; j++) {
        if (!blocksSeen.has(j) && overlaps(blocks[currentBlock], blocks[j])) {
          blocksToCheck.push(j)
        }
      }
    }
    blockGroups.push(blockGroup)
  }
  const blockColumns = new Array<number>(n).fill(0)
  const blockNumColumns = new Array<number>(n).fill(0)
  for (const blockGroup of blockGroups) {
    const blockEndTimes: number[] = []
    for (const block of [...blockGroup].sort((a, b) => blocks[a].start - blocks[b].start)) {
      let blockColumn = blockEndTimes.findIndex((end) => end <= blocks[block].start)
      if (blockColumn === -1) blockColumn = blockEndTimes.length
      blockColumns[block] = blockColumn
      blockEndTimes[blockColumn] = blocks[block].end
    }
    for (const block of blockGroup) {
      blockNumColumns[block] = blockEndTimes.length
    }
  }
  return blocks.map((block, i) => ({
    ...block,
    column: blockColumns[i],
    numColumns: blockNumColumns[i],
  }))
}

interface WeekViewProps {
  configs: EnrichedAIPhoneLineConfig[]
  colorOf: (config: EnrichedAIPhoneLineConfig) => string
  onClick: (index: number) => void
}

const WeekView: React.FC<WeekViewProps> = ({ configs, colorOf, onClick }) => (
  <Stack direction='row' className='week-view'>
    <Stack sx={{ pt: `${HOUR_HEIGHT * 0.75}px`, pr: 0.5 }}>
      {Array.from({ length: 24 }, (_, h) => (
        <Typography key={h} variant='caption' sx={{ height: HOUR_HEIGHT, textAlign: 'right' }}>
          {h === 0 ? '' : dayjs().hour(h).format('h A')}
        </Typography>
      ))}
    </Stack>
    {DAYS_OF_WEEK.map((day) => {
      const blocks: ConfigBlock[] = []
      configs.forEach((config, i) => {
        const start = toMinutes(config.start_time)
        const end = toMinutes(config.end_time)
        const isOvernight = end < start
        if (config.days_of_week?.includes(day.value)) {
          blocks.push({ configIndex: i, start, end: isOvernight ? 24 * 60 : end })
        }
        if (isOvernight && config.days_of_week?.includes((day.value + 6) % 7)) {
          blocks.push({ configIndex: i, start: 0, end })
        }
      })
      const layouts = computeBlockLayouts(blocks)
      return (
        <Stack key={day.value} sx={{ borderLeft: 1, borderColor: 'divider', flex: 1 }}>
          <Typography variant='label' align='center' sx={{ height: HOUR_HEIGHT * 0.75 }}>
            {day.label}
          </Typography>
          <Box sx={{ position: 'relative' }}>
            {Array.from({ length: 24 }, (_, h) => (
              <Box key={h} sx={{ height: HOUR_HEIGHT, borderTop: 1, borderColor: 'divider' }} />
            ))}
            {layouts.map(({ configIndex, start, end, column, numColumns }, i) => {
              const cfg = configs[configIndex]
              const name = cfg.name || `Configuration ${configIndex + 1}`
              const color = colorOf(cfg)
              return (
                <Box
                  key={i}
                  onClick={() => onClick(configIndex)}
                  title={name}
                  sx={{
                    position: 'absolute',
                    top: (start / 60) * HOUR_HEIGHT,
                    height: ((end - start) / 60) * HOUR_HEIGHT,
                    width: `${100 / numColumns}%`,
                    left: `${(column / numColumns) * 100}%`,
                    p: 0.5,
                    fontSize: 10,
                    overflow: 'hidden',
                    color,
                    bgcolor: alpha(color, 0.25),
                    border: `1px solid ${color}`,
                    borderRadius: 1,
                    cursor: 'pointer',
                    '&:hover': { bgcolor: alpha(color, 0.5) },
                  }}
                >
                  {name}
                </Box>
              )
            })}
          </Box>
        </Stack>
      )
    })}
  </Stack>
)

export default WeekView
